// eslint-disable-next-line import/no-cycle
import { contentIsChunked } from '../contentFramePortalGateInternalMethods/chunkedContent';
import type { RangyRange } from '../types/rangy';
import { extractStartAndEndIfValid } from './locationSerialization/base';
import { findChunkContainerForRange } from './locationSerialization/chunked';
import Renderer from './Renderer';

interface DomTraversalResult {
  allTextNodes: Node[];
  firstTextNode: Node | null;
}

/**
 * Computes the offset of a highlight within the document.
 *
 * This function also works correctly with chunked docs by counting the number of characters
 * before the chunk that the highlight is in, as well as the number of characters within the chunk.
 */
export default function computeHighlightOffset(
  expandedRange: RangyRange,
  renderer: Renderer,
): number {
  const fallbackOffset = expandedRange.getBookmark().start;

  if (!contentIsChunked()) {
    return fallbackOffset;
  }

  const chunkElement = findChunkContainerForRange(expandedRange, renderer.containerNode);
  if (!chunkElement) {
    return fallbackOffset;
  }

  let startContainer: Node;
  let startOffset: number;
  try {
    const extracted = extractStartAndEndIfValid(expandedRange, renderer.containerNode);
    startContainer = extracted.startContainer;
    startOffset = extracted.startOffset;
  } catch {
    return fallbackOffset;
  }

  const chunkIndex = parseInt(chunkElement.dataset.chunkIndex, 10);
  const precedingTextCharacterCount = countPrecedingTextCharacters(chunkIndex, renderer.containerNode);
  const intraChunkCharacterCount = countIntraChunkOffset(chunkElement, startContainer, startOffset);

  return precedingTextCharacterCount + intraChunkCharacterCount;
}

function countPrecedingTextCharacters(
  targetChunkIndex: number,
  rootElement: Document | Element,
): number {
  const chunkContainers = Array.from(rootElement.querySelectorAll('.rw-chunk-container'))
    .slice(0, targetChunkIndex);
  return chunkContainers.reduce((sum, container) => {
    const textCount = parseInt(container.getAttribute('data-text-character-count') || '0', 10);
    return sum + textCount;
  }, 0);
}

function countIntraChunkOffset(
  chunkElement: Element,
  startContainer: Node,
  startOffset: number,
): number {
  const { allTextNodes } = traverseAndCollectNodesFromDOM(chunkElement);

  let targetNode = startContainer;
  let localOffset = startOffset;

  // If the startContainer is not a text node, find the appropriate text node
  if (targetNode.nodeType !== Node.TEXT_NODE) {
    const firstTextNode = allTextNodes[0];
    if (firstTextNode) {
      targetNode = firstTextNode;
      localOffset = 0;
    } else {
      localOffset = 0;
    }
  }

  return getBackwardTextLength(allTextNodes, targetNode) + localOffset;
}

function traverseAndCollectNodesFromDOM(chunkElement: Element): DomTraversalResult {
  const allTextNodes: Node[] = [];
  let firstTextNode: Node | null = null;

  const walker = chunkElement.ownerDocument!.createTreeWalker(
    chunkElement,
    NodeFilter.SHOW_TEXT,
  );

  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      allTextNodes.push(node);
      firstTextNode ??= node;
    }
    node = walker.nextNode();
  }

  return { allTextNodes, firstTextNode };
}

function getBackwardTextLength(allTextNodes: Node[], targetNode: Node): number {
  let length = 0;

  for (const node of allTextNodes) {
    if (node === targetNode) {
      return length;
    }

    const textContent = node.textContent || '';
    length += textContent.length;
  }

  return 0;
}
